<template>
  <div class="relative flex-1">
    <!-- top header start -->
    <div class="bg-white border-b border-gray-200 py-6 sm:px-6 lg:px-8 px-5">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex-1 min-w-0">
          <h1 class="py-0.5 text-2xl font-semibold text-gray-900">
            {{ $t('Repair Orders') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-blue mr-4" to="/admin/repair-orders/new">{{ $t('Add standard repair') }}</router-link>
          <router-link class="btn btn-blue mr-4" to="/admin/repair-orders/new-manually">{{ $t('Add manual repair') }}</router-link>
          <div class="rounded-md shadow-sm mr-4">
            <button
              @click="openFiltersSidebar"
              class="inline-flex justify-center w-full rounded-md border border-gray-400 px-4 py-2 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
              id="filter-button"
              type="button"
            >
              <svg-vue class="self-center mr-3 h-4 w-4" icon="font-awesome.filter-regular"></svg-vue>
              {{ $t('Filters') }}
              <svg-vue class="self-center ml-2.5 -mr-1.5 h-3 w-3" icon="font-awesome.chevron-right-solid"></svg-vue>
            </button>
            <!-- side filters -->
            <div class="fixed inset-0 overflow-hidden z-30" v-show="filtersSidebar">
              <div class="absolute inset-0 overflow-hidden">
                <section :style="{ top: '65px' }" class="absolute inset-y-0 right-0 pl-10 max-w-full flex" v-on-clickaway="closeFiltersSidebar">
                  <transition
                    duration="500"
                    enter-active-class="transform transition ease-in-out duration-500"
                    enter-class="translate-x-full"
                    enter-to-class="translate-x-0"
                    leave-active-class="transform transition ease-in-out duration-500"
                    leave-class="translate-x-0"
                    leave-to-class="translate-x-full"
                  >
                    <div class="w-screen max-w-xs" v-show="filtersSidebar">
                      <div class="h-full flex flex-col space-y-4 bg-white shadow-xl">
                        <header class="px-4 pt-4 sm:px-6">
                          <div class="flex items-start justify-between space-x-3">
                            <h2 class=" text-lg leading-7 font-medium text-gray-900">
                              {{ $t('Filters') }}
                            </h2>
                            <div class="h-7 flex items-center">
                              <button
                                @click="closeFiltersSidebar"
                                aria-label="Close panel"
                                class="text-gray-400 hover:text-gray-900 transition ease-in-out duration-150"
                                type="button"
                              >
                                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </header>
                        <div class="relative flex-1 px-4 sm:px-6 h-full border-t overflow-y-scroll">
                          <div class="absolute inset-0 px-4 sm:px-6">
                            <div class="grid grid-cols-3 py-3">
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="label"
                                  >{{ $t('Search') }}</label
                                >
                                <div class="relative flex-grow focus-within:z-10">
                                  <div
                                    class="
                                      absolute
                                      inset-y-0
                                      left-0
                                      pl-3
                                      flex
                                      items-center
                                      pointer-events-none
                                    "
                                  >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                  </div>
                                  <input
                                    :placeholder="$t('Search')"
                                    @change="getRepairOrders"
                                    aria-label="Search"
                                    class="
                                      form-input
                                      block
                                      w-full
                                      pl-10
                                      text-sm
                                      transition
                                      ease-in-out
                                      duration-150
                                    "
                                    id="search"
                                    v-model.lazy="filters.search"
                                  />
                                </div>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="technician"
                                  >{{ $t('Technician') }}</label
                                >
                                <input-select
                                  :options="technicianList"
                                  @change="getRepairOrders"
                                  id="technician"
                                  option-label="name"
                                  v-model="filters.technician"
                                ></input-select>
                              </div>

                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="payment"
                                  >{{ $t('Payment') }}</label
                                >
                                <input-select
                                  :options="paymentStatuses"
                                  @change="getRepairOrders"
                                  id="payment"
                                  option-label="value"
                                  v-model="filters.payment"
                                ></input-select>
                              </div>

                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="is_lock"
                                  >{{ $t('Lock state') }}</label
                                >
                                <input-select
                                  :options="lockStatuses"
                                  @change="getRepairOrders"
                                  id="is_lock"
                                  option-label="value"
                                  v-model="filters.is_lock"
                                ></input-select>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="is_archive"
                                  >{{ $t('Archive state') }}</label
                                >
                                <input-select
                                  :options="archiveStatuses"
                                  @change="getRepairOrders"
                                  id="is_archive"
                                  option-label="value"
                                  v-model="filters.is_archive"
                                ></input-select>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="has_warranty"
                                  >{{ $t('Has warranty') }}</label
                                >
                                <input-select
                                  :options="warrantyStatuses"
                                  @change="getRepairOrders"
                                  id="has_warranty"
                                  option-label="value"
                                  v-model="filters.has_warranty"
                                ></input-select>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="label"
                                  >{{ $t('Status') }}</label
                                >
                                <input-select
                                  :options="statusList"
                                  @change="getRepairOrders"
                                  id="status"
                                  option-label="body"
                                  v-model="filters.status"
                                ></input-select>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="label"
                                  >{{ $t('Priority') }}</label
                                >
                                <input-select
                                  :options="priorityList"
                                  @change="getRepairOrders"
                                  id="priority"
                                  option-label="name"
                                  v-model="filters.priority"
                                ></input-select>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label
                                  class="
                                    block
                                    text-sm
                                    font-medium
                                    leading-5
                                    text-gray-700
                                  "
                                  for="label"
                                  >{{ $t('Sort') }}</label
                                >
                                <div class="relative inline-flex w-full">
                                  <button
                                    @click="changeSort"
                                    class="relative inline-flex items-center px-4 py-2 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700"
                                    type="button"
                                  >
                                    <svg-vue
                                      class="h-5 w-5 text-gray-400"
                                      icon="font-awesome.sort-amount-down-alt-regular"
                                      v-show="sort.order === 'asc'"
                                    ></svg-vue>
                                    <svg-vue
                                      class="h-5 w-5 text-gray-400"
                                      icon="font-awesome.sort-amount-up-alt-regular"
                                      v-show="sort.order === 'desc'"
                                    ></svg-vue>
                                    <span class="ml-2">{{ $t('Sort') }}</span>
                                  </button>
                                  <select
                                    @change="changeSort"
                                    aria-label="Sort by"
                                    class="block form-select w-full pl-3 pr-9 py-2 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                    id="sort.column"
                                    v-model="sort.column"
                                  >
                                    <option value="repair_status_id"> {{ $t('Status') }} </option>
                                    <option value="repair_priority_id">
                                      {{ $t('Priority') }}
                                    </option>
                                    <option value="user_id">
                                      {{ $t('Technician') }}
                                    </option>
                                    <option value="created_at">
                                      {{ $t('Created at') }}
                                    </option>
                                    <option value="id">
                                      {{ $t('Order Number') }}
                                    </option>
                                    <option value="updated_at">
                                      {{ $t('Updated at') }}
                                    </option>
                                    <option value="completed_at">
                                      {{ $t('Closed at') }}
                                    </option>
                                  </select>
                                </div>
                              </div>
                              <div class="col-span-3 mb-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="label">{{ $t('Items per page') }}</label>
                                <input-select
                                  :options="[
                                    { id: 5, name: 5 },
                                    { id: 10, name: 10 },
                                    { id: 25, name: 25 },
                                    { id: 50, name: 50 },
                                  ]"
                                  @change="getRepairOrders"
                                  id="per_page"
                                  option-label="name"
                                  required
                                  v-model="perPage"
                                ></input-select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </section>
              </div>
            </div>
            <!-- end -->
          </div>
          <button
            @click="getRepairOrders"
            class="rounded-md border border-gray-400 px-3 py-2 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
            type="button"
          >
            <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
          </button>
        </div>
      </div>
    </div>
    <!-- top header ends -->
    <!-- quick action widget start -->
    <div class="hidden sm:block">
      <div class="repair-order-list-toolbar" v-on-clickaway="closeQuickActionDropdown" v-show="selectedRows.length > 0">
        <div class="relative inline-block text-left">
          <button
            @click="toggleQuickActionDropdown('technician')"
            class="
              btn
              hover:bg-gray-100
              p-4
              border-r border-gray-200
              rounded-none
            "
            type="button"
          >
            <svg-vue class="h-6 w-6 text-blue-700" icon="font-awesome.user-tag-regular"></svg-vue>
          </button>
          <div
            class="
              origin-top-right
              absolute
              left-0
              mt-1
              w-56
              rounded-md
              shadow-lg
            "
            v-show="repairOrderActions.technician"
          >
            <div class="rounded-md bg-white shadow-xs">
              <div class="py-1">
                <template v-for="(technician, index) in technicianList">
                  <a
                    :key="index"
                    @click.prevent="quickAction('technician', technician.id)"
                    class="
                      block
                      px-4
                      py-2
                      text-sm text-gray-700
                      hover:bg-gray-100
                      hover:text-gray-900
                    "
                    href="#"
                    role="menuitem"
                  >
                    {{ technician.name }}
                    <small class="text-sm text-gray-900">[{{ technician.email }}]</small>
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
        <button
          @click="toggleQuickActionDropdown('delete')"
          class="
            btn
            hover:bg-gray-100
            p-4
            border-r border-gray-200
            rounded-none
          "
          type="button"
        >
          <svg-vue class="h-6 w-6 text-red-700" icon="font-awesome.trash-alt-regular"></svg-vue>
        </button>
      </div>
    </div>
    <!-- end -->
    <loading :status="loading"></loading>
    <!-- repair table start -->
    <div class="repair-orders-list">
      <!-- table  has rows-->
      <template v-if="repairOrderList.length > 0">
        <div class="sm:hidden">
          <ul class="border-b border-gray-200 divide-y divide-gray-200">
            <template v-for="(repairOrder, index) in repairOrderList">
              <li :key="index">
                <router-link
                  :to="`/admin/repair-orders/${repairOrder.uuid}/handle`"
                  class="
                    flex
                    items-center
                    justify-between
                    px-4
                    py-4
                    hover:bg-gray-100
                    sm:px-6
                  "
                >
                  <div class="flex items-center truncate space-x-3">
                    <img :alt="$t('Avatar')" :src="repairOrder.device.avatar" class="h-8 w-8 mr-4 rounded-full" />
                    <div class="whitespace-no-wrap">
                      <div class="text-sm leading-5 text-gray-900">
                        <span class="p-1 bg-gray-500 text-sm text-white">{{ repairOrder.brand.name | uppercase }}</span>
                        {{ repairOrder.device.name | capitalize }} [{{ repairOrder.device.model }}]
                      </div>
                      <div class="text-sm leading-5 text-gray-900 w-full truncate">
                        <span class="text-blue-900"> {{ $t('order') }}: {{ repairOrder.id }} | #{{ repairOrder.tracking }} </span>
                        | {{ $t('Issues') }} : {{ repairOrder.defects }} | {{ $t('Serial number') }}:
                        {{ repairOrder.serial_number }}
                      </div>
                    </div>
                  </div>
                  <svg-vue
                    class="
                      ml-4
                      h-5
                      w-5
                      text-gray-400
                      group-hover:text-gray-900 group-focus:text-gray-600
                      transition
                      ease-in-out
                      duration-150
                    "
                    icon="font-awesome.angle-right-regular"
                  ></svg-vue>
                </router-link>
              </li>
            </template>
          </ul>
        </div>
        <div class="hidden sm:block">
          <div
            class="
              align-middle
              inline-block
              min-w-full
              border-b border-gray-200
            "
          >
            <table class="min-w-full divide-y divide-gray-200">
              <caption class="hidden">
                Repair list
              </caption>
              <thead>
                <tr class="bg-blue-500 text-white">
                  <th class="px-3 pt-2 pb-3" scope="row">
                    <input
                      @change="selectAllRepairOrders"
                      aria-label="Checkbox"
                      class="
                        form-checkbox
                        cursor-pointer
                        h-4
                        w-4
                        text-blue-600
                        transition
                        duration-150
                        ease-in-out
                      "
                      id="select-all-repair-orders"
                      type="checkbox"
                      v-model="selectAll"
                    />
                  </th>
                  <th
                    class="
                      hidden
                      lg:table-cell
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                    "
                    scope="row"
                  >
                    {{ $t('Device') }}
                  </th>
                  <th
                    class="
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                      whitespace-no-wrap
                      overflow-x-auto
                    "
                    scope="row"
                  >
                    {{ $t('Information') }}
                  </th>
                  <th
                    class="
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                      whitespace-no-wrap
                      overflow-x-auto
                    "
                    scope="row"
                  >
                    {{ $t('Assign to') }}
                  </th>
                  <th
                    class="
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                      whitespace-no-wrap
                      overflow-x-auto
                    "
                    scope="row"
                  >
                    {{ $t('Status') }}
                  </th>
                  <th
                    class="
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                      whitespace-no-wrap
                      overflow-x-auto
                    "
                    scope="row"
                  >
                    {{ $t('Payment') }}
                  </th>
                  <th
                    class="
                      px-3
                      py-2
                      text-left text-xs
                      leading-4
                      font-medium
                      uppercase
                      tracking-wider
                      whitespace-no-wrap
                      overflow-x-auto
                    "
                    scope="row"
                  >
                    {{ $t('Updated at') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <template v-for="(repairOrder, index) in repairOrderList">
                  <router-link
                    :class="repairOrder.payment_status ? 'bg-white' : 'bg-red-100'"
                    :key="index"
                    :to="`/admin/repair-orders/${repairOrder.uuid}/handle`"
                    class="cursor-pointer hover:bg-blue-100"
                    tag="tr"
                  >
                    <td
                      class="
                        px-3
                        py-4
                        whitespace-no-wrap
                        text-center text-sm
                        leading-5
                        font-medium
                      "
                    >
                      <input
                        :id="'repair-' + repairOrder.id"
                        :value="repairOrder.id"
                        @change="selectRepairOrder"
                        @click.stop
                        aria-label="Checkbox"
                        class="
                          form-checkbox
                          cursor-pointer
                          h-4
                          w-4
                          text-blue-600
                          transition
                          duration-150
                          ease-in-out
                        "
                        type="checkbox"
                        v-model="selectedRows"
                      />
                    </td>
                    <td
                      class="
                        hidden
                        lg:table-cell
                        px-3
                        py-4
                        whitespace-no-wrap
                        leading-5
                      "
                    >
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10">
                          <img :alt="$t('Avatar')" :src="repairOrder.device.avatar" class="h-10 w-10 rounded-full" />
                        </div>
                        <div class="ml-4">
                          <div class="text-sm leading-5 font-medium text-gray-900">
                            <span class="p-1 bg-gray-500 text-sm text-white">{{ repairOrder.brand.name | uppercase }}</span>
                            {{ repairOrder.device.name | capitalize }}
                          </div>
                          <div class="text-sm leading-5 text-gray-900">[{{ repairOrder.device.model }}]</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-3 py-4 max-w-0 w-full whitespace-no-wrap">
                      <div
                        class="
                          flex
                          text-sm
                          leading-5
                          text-gray-900
                          w-full
                          truncate
                        "
                      >
                        <span class="text-blue-900"> {{ $t('Order') }}: {{ repairOrder.id }} | #{{ repairOrder.tracking }} </span>
                        | {{ $t('Issues') }} : {{ repairOrder.defects }} | {{ $t('Serial number') }}:
                        {{ repairOrder.serial_number }}
                      </div>
                      <div class="text-sm leading-5 text-gray-900 w-full truncate">{{ repairOrder.name }}({{ repairOrder.email }})</div>
                    </td>
                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                      <div class="text-sm leading-5 font-medium text-gray-900">
                        {{ repairOrder.assigned_to ? repairOrder.assigned_to.name : '' }}
                      </div>
                    </td>
                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                      <div class="text-sm leading-5 font-medium text-gray-900">
                        {{ repairOrder.status.body }}
                      </div>
                      <div class="text-sm leading-5 text-gray-900">
                        {{ repairOrder.priority.name }}
                      </div>
                    </td>
                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                      <div class="text-sm leading-5 font-medium text-gray-900">
                        <template v-if="repairOrder.payment_status">
                          <span class="bg-green-400 px-1 text-xs text-white">{{ $t('Paid') }}</span>
                        </template>
                        <template v-else>
                          <span class="bg-red-400 px-1 text-xs text-white">{{ $t('Unpaid') }}</span>
                        </template>
                      </div>
                      <div class="text-sm leading-5 text-gray-900">
                        {{ repairOrder.total_charges | currency }}
                      </div>
                    </td>
                    <td class="px-3 py-4 whitespace-no-wrap leading-5">
                      <div class="text-sm text-gray-900">
                        {{ repairOrder.updated_at | momentFormatDateTimeAgo }}
                      </div>
                    </td>
                  </router-link>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
      <!-- end -->
      <!-- no rows -->
      <template v-else="!loading">
        <div class="h-full flex">
          <div class="m-auto">
            <div class="grid grid-cols-1 justify-items-center h-full w-full p-4">
              <div class="flex justify-center items-center">
                <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
              </div>
              <div class="flex justify-center items-center">
                <div class="w-full font-semibold text-2xl">
                  {{ $t('No records found') }}
                </div>
              </div>
              <template v-if="anyFilter">
                <div class="flex justify-center items-center">{{ $t('Try changing the filters, or rephrasing your search') }}.</div>
              </template>
            </div>
          </div>
        </div>
      </template>
      <!-- end -->
    </div>
    <!-- table ends -->
    <!-- bottom nav start -->
    <nav
      class="
        bg-white
        absolute
        bottom-0
        left-0
        w-full
        px-4
        py-2
        flex
        items-center
        justify-between
        border-t border-blue-200
        sm:px-6
      "
    >
      <div class="hidden sm:block">
        <p class="text-sm leading-5 text-gray-700">
          {{ $t('Showing') }}
          <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
          {{ $t('to') }}
          <span class="font-medium">{{
            pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
          }}</span>
          {{ $t('of') }}
          <span class="font-medium">{{ pagination.total }}</span>
          {{ $t('results') }}
        </p>
      </div>
      <div class="flex-1 flex justify-between sm:justify-end">
        <button
          :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="pagination.currentPage <= 1"
          @click="changePage(pagination.currentPage - 1)"
          class="pagination-link"
          type="button"
        >
          {{ $t('Previous') }}
        </button>
        <button
          :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="pagination.currentPage >= pagination.totalPages"
          @click="changePage(pagination.currentPage + 1)"
          class="ml-3 pagination-link"
          type="button"
        >
          {{ $t('Next') }}
        </button>
      </div>
    </nav>
    <!-- bottom nav ends -->
    <!-- delete model start -->
    <repair-order-delete-model :show="repairOrderActions.delete" @cancelled="repairOrderActions.delete = false" @confirmed="quickAction('delete')">
      <template #title>{{ $t('Delete repair order') }}</template>
      {{ $t('Are you sure you want to delete the selected repair orders?') }}
      {{ $t('All data will be permanently removed') }}. {{ $t('All related data will be deleted') }}.
    </repair-order-delete-model>
    <!-- delete model end -->
  </div>
</template>
<script>
import { pickBy, identity } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import confirmationModel from '@/components/layout/admin/confirmation';

export default {
  name: 'list',
  components: {
    RepairOrderDeleteModel: confirmationModel,
  },
  metaInfo() {
    return {
      title: this.$i18n.t('Repair Orders'),
    };
  },
  mixins: [clickaway],
  data() {
    return {
      loading: true,
      filtersSidebar: false,
      filters: {
        search: null,
        technician: null,
        status: null,
        priority: null,
        payment: null,
        is_lock: null,
        is_archive: null,
        has_warranty: null,
      },
      repairOrderActions: {
        technician: false,
        delete: false,
      },
      sort: {
        order: 'desc',
        column: 'updated_at',
      },
      page: 1,
      perPage: 10,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      technicianList: [],
      statusList: [],
      priorityList: [],
      repairOrderList: [],
      selectAll: false,
      selectedRows: [],
      paymentStatuses: [
        { id: 0, value: this.$i18n.t('Unpaid') },
        { id: 1, value: this.$i18n.t('Paid') },
      ],
      lockStatuses: [
        { id: 0, value: this.$i18n.t('Unlocked') },
        { id: 1, value: this.$i18n.t('Locked') },
      ],
      archiveStatuses: [
        { id: 0, value: this.$i18n.t('None Archived') },
        { id: 1, value: this.$i18n.t('Archived') },
      ],
      warrantyStatuses: [
        { id: 0, value: this.$i18n.t('None warranty') },
        { id: 1, value: this.$i18n.t('with warranty') },
      ],
    };
  },
  computed: {
    anyFilter() {
      return pickBy(this.filters, identity);
    },
  },
  filters: {
    momentFormatDateTimeAgo(value) {
      return moment(value)
        .locale(window.app.app_date_locale)
        .fromNow();
    },
  },
  mounted() {
    this.getRepairOrders();
    this.getRepairOrderFilters();
  },
  methods: {
    openFiltersSidebar() {
      this.selectedRows = [];
      this.selectRepairOrder();
      setTimeout(() => {
        this.filtersSidebar = true;
      }, 100);
    },
    closeFiltersSidebar() {
      this.filtersSidebar = false;
    },
    closeQuickActionDropdown() {
      this.repairOrderActions.technician = false;
      this.repairOrderActions.delete = false;
    },
    toggleQuickActionDropdown(quickAction) {
      if (quickAction === 'technician') {
        this.repairOrderActions.technician = !this.repairOrderActions.technician;
        this.repairOrderActions.delete = false;
      }
      if (quickAction === 'delete') {
        this.repairOrderActions.delete = !this.repairOrderActions.delete;
        this.repairOrderActions.technician = false;
      }
    },
    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getRepairOrders();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getRepairOrders();
    },
    selectRepairOrder() {
      this.closeQuickActionDropdown();
      this.selectAll = Object.keys(this.selectedRows).length === Object.keys(this.repairOrderList).length;
    },
    selectAllRepairOrders() {
      this.selectedRows = [];
      this.closeQuickActionDropdown();
      for (const repair in this.repairOrderList) {
        if (this.selectAll) {
          this.selectedRows.push(this.repairOrderList[`${repair}`].id);
        }
      }
    },
    getRepairOrders() {
      this.selectAll = false;
      this.selectedRows = [];
      this.loading = true;
      axios
        .get(route('repair-orders.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            perPage: this.perPage,
            ...this.filters,
          },
        })
        .then((response) => {
          this.repairOrderList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getRepairOrders();
          } else {
            if (this.repairOrderList.length === 0) {
              this.selectAll = false;
              this.selectedRows = [];
            }
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getRepairOrderFilters() {
      this.loading = true;
      axios.get(route('repair-orders.filters')).then((response) => {
        this.technicianList = response.data.technicians;
        this.statusList = response.data.statuses;
        this.priorityList = response.data.priorities;
        this.loading = false;
      });
    },
    quickAction(param, value) {
      this.loading = true;
      axios
        .post(route('repair-orders.quick.actions'), {
          action: param,
          value,
          orders: this.selectedRows,
        })
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.getRepairOrders();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.closeQuickActionDropdown();
        });
    },
  },
};
</script>
<style lang="scss">
.repair-orders-list {
  @apply bg-white w-full z-0 overflow-y-auto;
  height: calc(100vh - 270px);
}

@media (min-width: theme('screens.md')) {
  .repair-orders-list {
    height: calc(100vh - 170px);
  }
}

.filters-options {
  @apply rounded-md bg-white p-3 overflow-y-auto;
  height: calc(100vh - 270px);
  max-height: 360px;
}

@media (min-width: theme('screens.md')) {
  .filters-options {
    height: calc(100vh - 140px);
  }
}
</style>
